import { useState } from 'react'
import { Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatFactValue } from '../utils/factsUtils'
import type { FactItem } from '../types'

interface FactCardProps {
  item: FactItem
}

function ValueDisplay({ item }: { item: FactItem }) {
  if (item.value.kind === 'boolean') {
    const isTrue = item.value.value === true
    return (
      <Badge
        variant="secondary"
        className={
          isTrue
            ? 'bg-green-100 text-green-800'
            : 'bg-muted text-muted-foreground'
        }>
        {formatFactValue(item.value)}
      </Badge>
    )
  }
  return <p>{formatFactValue(item.value)}</p>
}

export function FactCard({ item }: FactCardProps) {
  const [open, setOpen] = useState(false)

  const borderClass =
    item.confidence < 0.75
      ? 'border-l-4 border-l-red-500'
      : item.confidence < 1
        ? 'border-l-4 border-l-amber-500'
        : ''

  return (
    <div
      data-testid="fact-card"
      className={`rounded-md border bg-card px-3 py-2 text-sm ${borderClass}`}>
      <div className="flex items-start justify-between mb-1">
        <p className="text-muted-foreground text-xs">{item.name}</p>
        <button
          aria-label={open ? 'Hide source' : 'Show source'}
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setOpen((o) => !o)}>
          <Info className="h-3 w-3" />
        </button>
      </div>
      <ValueDisplay item={item} />
      {open && (
        <div className="mt-2 border-t pt-2 text-xs text-muted-foreground">
          <p>{item.raw_text}</p>
          <p className="mt-0.5">
            {Math.round(item.confidence * 100)}% confidence
          </p>
        </div>
      )}
    </div>
  )
}

/** Legend explaining the left-rail confidence colours. Shared by the sheet and the Facts tab. */
export function FactsLegend() {
  return (
    <p className="text-muted-foreground mb-2 flex items-center gap-3 text-[11px]">
      <span className="flex items-center gap-1">
        <span className="inline-block h-3 w-[3px] rounded-sm bg-amber-500" />
        75–99% confidence
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block h-3 w-[3px] rounded-sm bg-red-500" />
        &lt;75% confidence
      </span>
    </p>
  )
}
